// uncomment below line when ng serve
import { ActivatedRoute, Router } from '@angular/router';

// Code needed for serve or web components
import { Component, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceService } from '../../services/invoice/invoice.service';
import { combineLatest, finalize } from 'rxjs';
import { InvoiceLineItems } from '../../types/invoice';
import { TrpcService } from '../../services/trpc/trpc.service';
import { MessageService } from 'primeng/api';
import { contactBilling, CreateInvoiceRequest, CreateInvoiceRequestParams, InvoiceItem } from '../../../../../express-tRPC/src/api/invoices/types';
import { BillingSummaryItem, MonthlyBillingSummaryWidgetComponent } from 'src/app/components/monthly-billing-summary-widget/monthly-billing-summary-widget.component';
import { Identifier, KeycardInfo, LotSettings, SUREPARKSubscriber, TennatAccountContactInfo, VehicleInfo } from '../../../../../express-tRPC/src/shared/types';
import { getMatchingParkproContacts } from '../../../../../express-tRPC/src/shared/functions/match-parkpro-contacts';
import { DebugSettingsService } from '../../services/debug-settings/debug-settings.service';
import { Title } from '@angular/platform-browser';

export interface QueryParams {
  location: string,
  suite: string,
  account: string,
}

@Component({
  selector: 'app-invoice-details-page',
  templateUrl: './monthly-invoice-details-page.component.html',
  styleUrl: './monthly-invoice-details-page.component.scss'
})
export class MonthlyInvoiceDetailsPageComponent {

  filename = `monthly-invoice-details-page.component.ts`;

  queryParams!: QueryParams;

  @Input() location!: string;
  @Input() suite!: string;
  @Input() account!: string;

  ARTypeOptions: any[] = [
    { code: "Monthly", name: "Monthly"},
    { code: "Validation", name: "Validation"},
    { code: "Key Card", name: "Key Card"},
    { code: "Special", name: "Special"}
  ];

  invoiceDetails: any = undefined;
  loadingInvoiceDetails: boolean = false;
  invoiceForm!: FormGroup;

  //
  // Why are there two invoice line items?
  //
  invoiceLineItems: InvoiceLineItems[] = []; // This value is passed into the child component
  invoice_line_items: InvoiceLineItems[] = []; // Structure clone of invoiceLineItems

  showDevPanel: boolean = false;
  // Show developer tools only in development environments
  // Can be controlled via environment file or manually toggled here
  showDevTools: boolean = false;
  saveInvoiceRequest: any = {};
  generatingInvoice: boolean = false;

  today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

  locationSettings!: LotSettings;
  allContacts: TennatAccountContactInfo[] = [];
  allSureparkMembers: SUREPARKSubscriber[] = [];
  matchingSureparkMembers: SUREPARKSubscriber[] = [];
  completeSureparkMembers: SUREPARKSubscriber[] = [];
  inCompleteSureparkMembers: SUREPARKSubscriber[] = [];
  missingSureparkMembers: SUREPARKSubscriber[] = [];
  mergingContact: boolean = false;

  // Properties for adding missing SUREPARK members
  showAddMissingContactsDialog: boolean = false;
  addingMissingContacts: boolean = false;
  selectedMissingMembers: boolean[] = [];
  selectAllMissing: boolean = false;
  selectedDefaultRate: string = '';
  availableRates: any[] = [];

  refreshBillingSummary: boolean = false;

  @ViewChild(MonthlyBillingSummaryWidgetComponent) monthlyBillingSummaryWidgetComponent!: MonthlyBillingSummaryWidgetComponent;

  constructor(
    // uncomment below line when ng serve
    private router: Router,
    public route: ActivatedRoute,

    // Code needed for serve or web components
    private messageService: MessageService,
    private fb: FormBuilder,
    private invoiceService: InvoiceService,
    private trpcService: TrpcService,
    private debugSettingsService: DebugSettingsService,
    private titleService: Title
  ) { }

  ngOnInit(): void {
    // Check debug settings
    this.showDevTools = this.debugSettingsService.isMonthlyBillingDebugEnabled();

    this.invoiceForm = this.fb.group({
      suite: [{disabled: true, value: ''}, Validators.required],
      accountnumber: [{disabled: true, value: ''}, Validators.required],
      billto: [{disabled: true, value: ''}],
      type: [''],
      invoice_one_time_charges_only: [false],
      notes: [''],
    });

    combineLatest([this.trpcService.currentLocationObservable, this.route.params]).subscribe(async ([result1, result2]) => {
      if(result1 && result2) {
        this.location = result1;
        // this.titleService.setTitle("Update Tenant Info | MPI Portal");

        this.loadingInvoiceDetails = true;
        this.queryParams = result2 as QueryParams;

        this.titleService.setTitle(`Update Monthly Profile | ${this.queryParams.suite}-${this.queryParams.account} Loc ${this.location}`);

        await this.getSettings();
        await this.loadSureparkMembers();
        this.getInvoiceData(this.queryParams);

      }
    });

    // if(environment.buildMode === 'serve') {
    //   this.route.params.subscribe((queryParams) => {
    //     this.loadingInvoiceDetails = true;
    //     this.queryParams = queryParams as QueryParams;
    //     this.getInvoiceData(this.queryParams);
    //   })
    // }

    // if(environment.buildMode === 'web-component') {
    //   this.loadingInvoiceDetails = true;
    //   this.queryParams = {location: this.location, suite: this.suite, account: this.account};
    //   this.getInvoiceData(this.queryParams);
    // }

  }

  get invoiceFormControls(): any {
    return this.invoiceForm.controls;
  }

  async getSettings() {

    let locationSettingsAny: any = await this.trpcService.trpc.settings.get.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });

    this.locationSettings = locationSettingsAny as LotSettings;

    console.log(`Location settings: `, this.locationSettings);

  }

  async loadSureparkMembers() {
    let sureparkMembersResult: any = await this.trpcService.trpc.surepark.getSureparkGroupsSubscribers.query().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
        key: 'general-message'
      });
    });

    if(sureparkMembersResult) {
      this.allSureparkMembers = sureparkMembersResult;
    }
  }

  private getInvoiceData(params: QueryParams) {
    this.invoiceService.getInvoiceDetails(params)
    .pipe(finalize(() => { this.loadingInvoiceDetails = false; }))
    .subscribe(async (response: any) => {

      // console.log(`getInvoiceDetails response`, response);

      if (response?.status === 'error') {
        console.error('Error fetching invoice details:', response);
        this.messageService.add({
          severity: "error",
          detail: response?.error,
        });
        return;
      }

      // if(response['ver'] === "2") {
      //   let invoicesResult: any = await this.trpcService.trpc.monthly_invoice.getMonthlyInvoicesDetails.query({ lid: params.location, suite: params.suite, accountnumber: params.account }).catch((error: any) => {
      //     this.messageService.add({
      //       severity: "error",
      //       detail: error.message,
      //     });
      //   });
      //   if(invoicesResult?.length) {
      //     let data = JSON.parse(invoicesResult[0]['data'].replaceAll("\r", "\\r").replaceAll("\n", "\\n"));
      //     data['items'] = data['invoice_line_items'];
      //     data['invoicenotes'] = data['notes'];
      //     this.invoiceDetails = data;
      //   }

      // } else {
        this.invoiceDetails = response;
      // }

      if(this.invoiceDetails) {
        this.invoiceFormControls['suite'].setValue(this.invoiceDetails.suite);
        this.invoiceFormControls['accountnumber'].setValue(this.invoiceDetails.accountnumber);
        this.invoiceFormControls['billto'].setValue(this.invoiceDetails.billto);

        this.invoiceFormControls['type'].setValue(this.invoiceDetails.type);
        this.invoiceFormControls['notes'].setValue(this.invoiceDetails.invoicenotes);
        this.invoiceFormControls['invoice_one_time_charges_only'].setValue(this.invoiceDetails.invoice_one_time_charges_only || false);
        
        //
        // Take the received line items and include all except the contactBilling items.
        // Remember that contact billing stores the line items for the contacts as line items so that it can be billed
        // This data is the invoices one time charges.
        //
        if(this.invoiceDetails['items']?.length) {
          this.invoiceLineItems = this.invoiceDetails['items']
            .filter((item: any) => !item.contactBilling)
            .map((item: any) => {
            return {
              "qty": item['qty'],
              "desc": item['description'],
              "rate": item['unitprice'],
              "amount": (item['unitprice'] * item['qty']),
              "type": item['line_ar_type']
            }
          })
          this.invoice_line_items = structuredClone(this.invoiceLineItems);
        }

        await this.getProfileContacts(this.invoiceDetails);
        await this.getMatchingParkproContacts();
      }
    })
  }

  async getProfileContacts(monthlyProfile: any): Promise<void> {

    await this.trpcService.trpc.surepark.getAllProfileContacts.query({ suite: String(monthlyProfile.suite), account: String(monthlyProfile.accountnumber), checkExpired: false }).then((allContactsResult) => {
      if (allContactsResult) {
        this.allContacts = allContactsResult;
      }
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: "Unable to load contacts of profile (" + monthlyProfile.suite + " - " + monthlyProfile.accountnumber + ")",
        key: 'api-errors'
      });
    });

  }

  async getMatchingParkproContacts() {

    this.messageService.clear('contact-messages');

    const fn = `getMatchingParkproContacts`;
    console.log(`${this.filename} ${fn} entering`);

    const parkingDeviceGroupID = this.invoiceDetails['parkingDeviceGroupID'];

    if(!this.invoiceDetails || parkingDeviceGroupID === '') {
      return;
    }

    try {
      // Use the shared function directly for client-side matching
      const result = getMatchingParkproContacts(
        Number(this.location),
        String(this.queryParams.suite),
        String(this.queryParams.account),
        this.invoiceDetails['parkingDeviceGroupID'],
        this.allContacts,
        this.allSureparkMembers,
        this.locationSettings
      );

      // Use the returned data to populate the component properties
      this.matchingSureparkMembers = result.matchingSureparkMembers;
      this.completeSureparkMembers = result.completeSureparkMembers;
      this.inCompleteSureparkMembers = result.incompleteSureparkMembers;
      this.missingSureparkMembers = result.missingSureparkMembers;

      console.log('Matching results from client-side function:', {
        matching: this.matchingSureparkMembers,
        complete: this.completeSureparkMembers,
        incomplete: this.inCompleteSureparkMembers,
        missing: this.missingSureparkMembers
      });
    } catch (error: any) {
      console.error('Error getting matching ParkPro contacts:', error);
      this.messageService.add({
        severity: "error",
        detail: error?.message || "Failed to get matching ParkPro contacts",
        key: 'contact-messages'
      });
    }

    // Controls whether a summary of the found, matched, different rates, and missing contacts is displayed
    const debugShowMatchingSummary = false;

    if (debugShowMatchingSummary) {
      this.messageService.addAll([{
        severity: "success",
        detail: `${this.matchingSureparkMembers.length} surepark member(s) found for this profile.`,
        key: "contact-messages",
        closable: false
      }, {
        severity: "success",
        detail: `${this.completeSureparkMembers.length} surepark member(s) are matched.`,
        key: "contact-messages",
        closable: false
      }, {
        severity: "warn",
        detail: `${this.inCompleteSureparkMembers.length} member(s) has different rates.`,
        key: "contact-messages",
        closable: false
      }, {
        severity: "error",
        detail: `${this.missingSureparkMembers.length} surepark member profile(s) are missing.`,
        key: "contact-messages",
        closable: false
      }]);
    }
  }


  async getMatchingSureparkSubscriber(profileContact: TennatAccountContactInfo): Promise<SUREPARKSubscriber | undefined> {
    return this.matchingSureparkMembers.find((member: SUREPARKSubscriber, memberIndex: number) => {

      const keycardIdentifiers = member.identifiers.filter((identifier: Identifier) => identifier.type == "Hid");
      const vehicleIdentifiers = member.identifiers.filter((identifier: Identifier) => identifier.type == "PlateNumber");

      // ✅ Check if at least one keycard matches
      const hasKeycardMatch = profileContact?.keycardsInfo?.some((ppidf: KeycardInfo) =>
        keycardIdentifiers.some((spidf: Identifier) => spidf.identifierNumber === `${ppidf.facility_code}/${ppidf.keycards}`)
      );

      // ✅ Check if at least one vehicle matches
      const hasVehicleMatch = profileContact?.vehiclesInfo?.some((ppidf: VehicleInfo) =>
        vehicleIdentifiers.some((spidf: Identifier) => spidf.identifierNumber === ppidf.plate)
      );
      return hasKeycardMatch || hasVehicleMatch;
    });
  }

  contactUpdated(event: boolean) {
    this.refreshBillingSummary = event;
  }

  updateLineItems(lineItems: any) {
    this.invoice_line_items = lineItems;
  }

  get getCurrentMonthlyCharges(): number {
    const billablecontacts = this.monthlyBillingSummaryWidgetComponent?.getBillableContacts();
    const total = billablecontacts?.reduce((sum, item) => sum + Number(item.rate_amount), 0);
    return total;
  }

  get getTotalOneTimeCharges(): number {
    const total = this.invoice_line_items.reduce((sum, item) => sum + ((item.qty || 0) * (item.rate || 0)), 0);
    return total;
  }

  get getInvoiceTotalAmount(): number {

    const invoiceOneTimeChargesOnly = this.invoiceForm.get('invoice_one_time_charges_only')?.value;
    
    //The total on the invoice should ignore any keycards if `invoiceOneTimeChargesOnly` is TRUE
    if(invoiceOneTimeChargesOnly) {
      const totalOneTimeCharges = this.getTotalOneTimeCharges;
      return totalOneTimeCharges;
    } else {
      const billablecontacts = this.monthlyBillingSummaryWidgetComponent?.getBillableContacts();
      const totalOneTimeCharges = this.getTotalOneTimeCharges;
      const total = billablecontacts?.reduce((sum, item) => sum + Number(item.rate_amount), 0);

      return total + totalOneTimeCharges;
    }
  }

  async saveInvoiceData(): Promise<void> {

    const validation = this.validateRequiredFields();
    if (!validation.isValid) {
      this.messageService.add({
        severity: "error",
        detail: `Please fill in the following required fields: ${validation.missingFields.join(', ')}.`,
      });
      return;
    }

    const invoiceOneTimeChargesOnly = this.invoiceForm.get('invoice_one_time_charges_only')?.value;
    if (invoiceOneTimeChargesOnly && !this.invoice_line_items.length) {
      this.messageService.add({
        severity: "error",
        detail: `Please add atleast one line item.`,
      });
      return;
    }

    this.generatingInvoice = true;

    // First, validate the invoice for SUREPARK member issues
    try {
      const validationResult = await this.trpcService.trpc.monthly_invoice.validateInvoicesReadyToSend.query({
        suite: this.queryParams.suite,
        accountnumber: this.queryParams.account,
        invoiceOneTimeChargesOnly: invoiceOneTimeChargesOnly
      });

      // Check if there are any issues with this specific invoice
      let hasIssues = false;
      let issueDetails = null;

      if (validationResult && validationResult.invoicesWithIssues > 0 && validationResult.issues) {
        // Find issues for this specific invoice
        issueDetails = validationResult.issues.find((issue: any) =>
          String(issue.suite) === String(this.queryParams.suite) &&
          String(issue.account) === String(this.queryParams.account)
        );

        if (issueDetails) {
          hasIssues = true;
        }
      }

      if (hasIssues && issueDetails) {
        // Build warning message
        let warningMessage = `This invoice has issue(s):\n`;

        if(issueDetails.message) {
          warningMessage += `• ${issueDetails.message}\n`;
        }

        if (issueDetails.missingSureparkMembers > 0) {
          warningMessage += `• ${issueDetails.missingSureparkMembers} SUREPARK member(s) are missing from ParkPro\n`;
        }

        if (issueDetails.incompleteSureparkMembers > 0) {
          warningMessage += `• ${issueDetails.incompleteSureparkMembers} SUREPARK member(s) have incorrect rates\n`;
        }

        warningMessage += `\nDo you still want to save this invoice? (You may need to fix these issues before billing)`;

        // Ask user for confirmation
        if (!confirm(warningMessage)) {
          this.messageService.add({
            severity: "warn",
            summary: "Save Cancelled",
            detail: "Invoice was not saved due to validation issues. Please fix the SUREPARK member issues and try again."
          });
          this.generatingInvoice = false;
          return;
        }
      }
    } catch (error: any) {
      console.error('Error validating invoice:', error);
      // If validation fails, warn but allow save to continue
      const continueAnyway = confirm('Unable to validate invoice. Do you want to continue saving anyway?');
      if (!continueAnyway) {
        this.generatingInvoice = false;
        return;
      }
    }

    this.saveInvoiceRequest = this.invoiceForm.getRawValue();
    this.saveInvoiceRequest['lid'] = this.queryParams.location;
    this.saveInvoiceRequest['invoice_line_items'] = this.invoice_line_items.map((item: any) => {
      return {
        "qty": item['qty'],
        "description": item['desc'],
        "unitprice": item['rate'],
        "amount": (item['rate'] * item['qty']),
        "line_ar_type": item['type']
      }
    });
    this.saveInvoiceRequest['invoiceOneTimeChargesOnly'] =this.saveInvoiceRequest['invoice_one_time_charges_only'];

    this.trpcService.trpc.monthly_invoice.saveContact.mutate(this.saveInvoiceRequest).then((response: any) => {
      this.messageService.add({
        severity: "success",
        detail: "Invoice has been saved.",
      });
    })
    .catch((error: any) => {
      
      console.log(error);

      this.messageService.add({
        severity: "error",
        detail: error.message,
        sticky: true
      });
    }).finally(() => {
      this.generatingInvoice = false;
    });

  }

  async billMonthlyInvoice(): Promise<void> {
    // Call the createWebInvoiceFromUI tRPC function

    const validation = this.validateRequiredFields();
    if (!validation.isValid) {
      this.messageService.add({
        severity: "error",
        detail: `Please fill in the following required fields: ${validation.missingFields.join(', ')}.`,
      });
      return;
    }

    const isValidLineItems:boolean = this.validateLineItems();
    if(!isValidLineItems) {
      this.messageService.add({
        severity: "error",
        detail: "Please enter valid details in line items.",
      });
      return;
    }

    const billableContacts: TennatAccountContactInfo[] = this.monthlyBillingSummaryWidgetComponent.getBillableContacts();
    const billingSummary: BillingSummaryItem[] = this.monthlyBillingSummaryWidgetComponent.getCurrentMonthBillingSummary();


    this.generatingInvoice = true;

    const currentDate: Date = new Date();
    const saveInvoiceRequest = this.invoiceForm.getRawValue();
    let invoiceItems: InvoiceItem[] = this.invoice_line_items.map((item: InvoiceLineItems) => {
      const invoiceItem: InvoiceItem = {
        qty: String(item.qty),
        description: item.desc,
        textDescription: '',
        unitprice: String(item.rate),
        line_ar_type: ''
      };
      return invoiceItem;
    });

    billingSummary.forEach((summary: BillingSummaryItem) => {
      const contactGroupByRate = billableContacts.filter((contact: TennatAccountContactInfo) => contact.rate_desc == summary.item);

      const invoiceItem: InvoiceItem = {
        qty: String(summary.qty),
        description: summary.item,
        textDescription: '',
        unitprice: String(summary.amount / summary.qty),
        line_ar_type: '',
        contactBilling: this.getBillingContactsForInvoice(contactGroupByRate)
      };
      invoiceItems.push(invoiceItem);
    });

    const CreateInvoiceRequestParams: CreateInvoiceRequestParams = {
      type: saveInvoiceRequest['type'],
      suite: saveInvoiceRequest['suite'],
      invoicenotes: saveInvoiceRequest['notes'],
      accountnumber: saveInvoiceRequest['accountnumber'],
      billingMonth: currentDate.toLocaleString('default', { month: 'long' }) + "-" + currentDate.getFullYear(), //e.g. "March-2025",
      date: currentDate.toLocaleDateString("en-US"),
      items: invoiceItems,
      parkingDeviceGroupID: this.invoiceDetails['tenant']?.['parkingDeviceGroupID'] || ''
    };

    const createInvoiceRequest: CreateInvoiceRequest = {
      tickets: [],
      tenant: this.invoiceDetails['tenant'],
      comments: '',
      params: CreateInvoiceRequestParams
    }

    this.trpcService.trpc.monthly_invoice.createWebInvoiceFromUI.mutate(createInvoiceRequest).then((response: any) => {
      console.log('createWebInvoiceFromUI response:', response);
      this.generatingInvoice = false;
      this.messageService.add({
        severity: "success",
        detail: "Monthly invoice billing initiated successfully.",
      });
    })
    .catch((error: any) => {
      this.generatingInvoice = false;
      console.error('Error billing monthly invoice:', error);
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    });
  }

  /**
   * Utility function to compare dates ignoring timezone differences
   * Compares only the date portion (year, month, day)
   * @param date1 - First date (can be string or Date)
   * @param date2 - Second date (can be string or Date)
   * @returns true if dates are the same ignoring time and timezone
   */
  private areDatesEqualIgnoringTimezone(date1: string | Date | null, date2: string | Date | null): boolean {
    // Handle null/undefined cases
    if (!date1 || !date2) {
      return date1 === date2;
    }

    try {
      // Convert to Date objects if strings
      const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
      const d2 = typeof date2 === 'string' ? new Date(date2) : date2;

      // Check if dates are valid
      if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
        return false;
      }

      // Compare year, month, and day only
      return d1.getFullYear() === d2.getFullYear() &&
             d1.getMonth() === d2.getMonth() &&
             d1.getDate() === d2.getDate();
    } catch (error) {
      console.error('Error comparing dates:', error);
      return false;
    }
  }

  /**
   * Bill the monthly invoice using the backend process
   * This calls the createMonthlyInvoicesForLocation endpoint with specific suite and account
   * @param dryRun - If true, runs in dry run mode (no actual invoice creation)
   */
  async billMonthlyInvoiceBackend(dryRun: boolean = false): Promise<void> {
    // Validate required fields first
    const validation = this.validateRequiredFields();
    if (!validation.isValid) {
      this.messageService.add({
        severity: "error",
        detail: `Please fill in the following required fields: ${validation.missingFields.join(', ')}.`,
      });
      return;
    }

    const isValidLineItems: boolean = this.validateLineItems();
    if (!isValidLineItems) {
      this.messageService.add({
        severity: "error",
        detail: "Please enter valid details in line items.",
      });
      return;
    }

    // Ensure we have the required invoice details
    if (!this.queryParams || !this.queryParams.suite || !this.queryParams.account) {
      this.messageService.add({
        severity: "error",
        detail: "Missing suite or account information.",
      });
      return;
    }

    this.generatingInvoice = true;

    // Define the billing pending date constant (must match backend)
    const BILLING_PENDING_DATE = '1970-01-01 00:00:00';

    try {
      // First, check if the invoice is properly queued for billing
      const invoiceDetails: any = await this.trpcService.trpc.monthly_invoice.getMonthlyInvoicesDetails.query({
        lid: String(this.queryParams.location),
        suite: String(this.queryParams.suite),
        accountnumber: String(this.queryParams.account)
      });

      if (!invoiceDetails || !invoiceDetails[0]) {
        this.generatingInvoice = false;
        this.messageService.add({
          severity: "error",
          detail: "Monthly invoice profile not found. Please save the profile first.",
        });
        return;
      }

      const monthlyInvoice = invoiceDetails[0];

      // Check if the invoice is queued (last_billed should equal BILLING_PENDING_DATE)
      // Use date comparison that ignores timezone differences
      const isQueued = this.areDatesEqualIgnoringTimezone(monthlyInvoice.last_billed, BILLING_PENDING_DATE);

      if (!isQueued) {
        this.generatingInvoice = false;

        // Provide helpful message based on the current status
        let detailMessage = "The monthly invoice is not queued for billing. ";

        if (monthlyInvoice.last_billed === null || monthlyInvoice.last_billed === '') {
          detailMessage += "Please queue the invoice from the Monthly Invoice List page first or use the 'Queue for Billing' button.";
        } else {
          const lastBilledDate = new Date(monthlyInvoice.last_billed);
          detailMessage += `This invoice was last billed on ${lastBilledDate.toLocaleDateString()}. Please use the 'Queue for Billing' button to queue it for processing.`;
        }

        this.messageService.add({
          severity: "warn",
          summary: "Invoice Not Queued",
          detail: detailMessage,
        });
        return;
      }

      console.log(`Invoice is properly queued for billing (last_billed matches BILLING_PENDING_DATE)${dryRun ? ' - DRY RUN MODE' : ''}`);

      // Check if the bill_status is not 'hold'
      if (monthlyInvoice.bill_status === 'hold') {
        this.generatingInvoice = false;
        this.messageService.add({
          severity: "warn",
          summary: "Invoice On Hold",
          detail: "This monthly invoice is on hold. Please remove the hold status before billing.",
        });
        return;
      }

      // Invoice is properly queued, now save any recent changes (skip in dry run mode)
      if (!dryRun) {
        await this.saveInvoiceData();
      }

      // Call the backend process to create the invoice for this specific suite and account
      const result = await this.trpcService.trpc.invoice.createMonthlyInvoicesForLocation.mutate({
        suite: Number(this.queryParams.suite),
        account: Number(this.queryParams.account),
        limit: 1,
        dryRun: dryRun
      });

      this.generatingInvoice = false;

      if (result && result.success && result.processed > 0) {
        const modeText = dryRun ? ' (DRY RUN)' : '';
        this.messageService.add({
          severity: "success",
          summary: `Invoice ${dryRun ? 'Simulated' : 'Created'}${modeText}`,
          detail: `Monthly invoice ${dryRun ? 'would be' : 'was'} created successfully for suite ${this.queryParams.suite}, account ${this.queryParams.account}.${dryRun ? ' No actual invoice was created.' : ''}`,
        });

        // Log the results for debugging
        console.log(`Backend invoice creation results${modeText}:`, result);

        // Refresh the invoice data to show updated status (skip in dry run since nothing changed)
        if (!dryRun) {
          await this.getInvoiceData(this.queryParams);
        }
      } else {
        this.messageService.add({
          severity: "warn",
          detail: `No monthly invoice was processed${dryRun ? ' (DRY RUN)' : ''}. Please verify the invoice status and try again.`,
        });
      }
    } catch (error: any) {
      this.generatingInvoice = false;
      console.error(`Error billing monthly invoice via backend${dryRun ? ' (DRY RUN)' : ''}:`, error);
      this.messageService.add({
        severity: "error",
        detail: error.message || `Failed to ${dryRun ? 'simulate' : 'create'} monthly invoice via backend process${dryRun ? ' (DRY RUN)' : ''}.`,
      });
    }
  }

  /**
   * Queue the monthly invoice for billing by resetting last_billed to BILLING_PENDING_DATE
   * This allows the invoice to be processed by the monthly billing process
   */
  async queueInvoiceForBilling(): Promise<void> {
    // Ensure we have the required invoice details
    if (!this.queryParams || !this.queryParams.suite || !this.queryParams.account) {
      this.messageService.add({
        severity: "error",
        detail: "Missing suite or account information.",
      });
      return;
    }

    // Get current month and year for the next_billed field
    const currentDate = new Date();
    const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const year = currentDate.getFullYear();

    // Confirm with the user
    const confirmMessage = `Queue this invoice (Suite ${this.queryParams.suite}, Account ${this.queryParams.account}) for billing in ${currentDate.toLocaleString('default', { month: 'long' })} ${year}?`;

    if (!confirm(confirmMessage)) {
      return;
    }

    this.generatingInvoice = true;

    try {
      // Call the tRPC endpoint to queue the specific invoice
      const result = await this.trpcService.trpc.invoice.queueSpecificMonthlyInvoice.mutate({
        suite: Number(this.queryParams.suite),
        account: Number(this.queryParams.account),
        month: month,
        year: year
      });

      this.generatingInvoice = false;

      if (result.success) {
        this.messageService.add({
          severity: "success",
          summary: "Invoice Queued",
          detail: `Invoice for suite ${this.queryParams.suite}, account ${this.queryParams.account} has been queued for billing.`,
        });

        // Refresh the invoice data to show updated status
        await this.getInvoiceData(this.queryParams);
      } else {
        this.messageService.add({
          severity: "warn",
          summary: "Queue Failed",
          detail: result.message || "Unable to queue the invoice. It may be on hold or not found.",
        });
      }
    } catch (error: any) {
      this.generatingInvoice = false;
      console.error('Error queueing invoice for billing:', error);
      this.messageService.add({
        severity: "error",
        detail: error.message || "Failed to queue invoice for billing.",
      });
    }
  }

  getBillingContactsForInvoice(contactGroupByRate: TennatAccountContactInfo[]) {

    let contactByKeycard: contactBilling[] = [];

    for (let index = 0; index < contactGroupByRate.length; index++) {

      const contact = contactGroupByRate[index];
      const keycards = contact?.keycardsInfo || [];

      for (let index = 0; index < keycards.length; index++) {
        const keycard = keycards[index];

        contactByKeycard.push({
          name: contact.contactName,
          effectiveDate: contact.effectiveDates,
          terminationDate: contact.terminationDates,
          keycards: keycard['keycards']!
        });
      }
    }
    return contactByKeycard;

  }

  validateRequiredFields(): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    // Define field display names mapping
    const fieldLabels: { [key: string]: string } = {
      'suite': 'Suite',
      'accountnumber': 'Account Number',
      'type': 'Type',
      'billto': 'Bill To',
      'notes': 'Notes'
    };

    // Check each form control that has required validator
    Object.keys(this.invoiceForm.controls).forEach(controlName => {
      const control = this.invoiceForm.get(controlName);
      if (control && control.hasError('required')) {
        const displayName = fieldLabels[controlName] || controlName;
        missingFields.push(displayName);
      }
    });

    return {
      isValid: missingFields.length === 0,
      missingFields: missingFields
    };
  }

  validateLineItems(): boolean {

    //If no `one time charges` found then return valid because its not required
    if(!this.invoice_line_items.length) return true;
    //If there is items in `One time charges` then check every item has valid field value
    return this.invoice_line_items.every((lineItem: any) => lineItem['qty'] && lineItem['desc'])
  }

  // Methods for adding missing SUREPARK members
  openAddMissingContactsDialog(): void {
    this.showAddMissingContactsDialog = true;
    // Initialize selected members array
    this.selectedMissingMembers = new Array(this.missingSureparkMembers.length).fill(false);
    this.selectAllMissing = false;

    // Load available rates for selection
    this.loadAvailableRates();
  }

  async loadAvailableRates(): Promise<void> {
    try {
      // Get available rates from the invoice service or trpc service
      const response: any = await this.trpcService.trpc.authRouter.getTenantContactServiceInfo.query().catch((error: any) => {
        console.error('Error loading rates:', error);
      });

      if (response && response.available_invoice_items) {
        // Filter for monthly rates
        this.availableRates = response.available_invoice_items.filter((item: any) =>
          item.type === 'MONTHLY' || item.type === 'Monthly'
        );

        // Set default rate if available
        if (this.availableRates.length > 0) {
          this.selectedDefaultRate = this.availableRates[0].code;
        }
      }
    } catch (error) {
      console.error('Error loading available rates:', error);
    }
  }

  toggleSelectAll(event: any): void {
    this.selectedMissingMembers = new Array(this.missingSureparkMembers.length).fill(event.checked);
  }

  hasSelectedMembers(): boolean {
    return this.selectedMissingMembers.some(selected => selected);
  }

  getMemberKeycards(member: SUREPARKSubscriber): string[] {
    return member.identifiers
      .filter(id => id.type === 'Hid')
      .map(id => id.identifierNumber);
  }

  getMemberVehicles(member: SUREPARKSubscriber): string[] {
    return member.identifiers
      .filter(id => id.type === 'PlateNumber')
      .map(id => id.identifierNumber);
  }

  convertSureparkMemberToContact(member: SUREPARKSubscriber, rate: any): TennatAccountContactInfo {
    // Extract keycards and vehicles from identifiers
    const keycardIdentifiers = member.identifiers.filter(id => id.type === 'Hid');
    const vehicleIdentifiers = member.identifiers.filter(id => id.type === 'PlateNumber');

    // Convert keycards to KeycardInfo format
    const keycardsInfo: KeycardInfo[] = keycardIdentifiers.map(id => {
      const [facilityCode, keycard] = id.identifierNumber.split('/');
      return {
        keycards: keycard,
        facility_code: facilityCode,
        active_keycard: true,
        keycard_action: '',
        is_new: true
      };
    });

    // Convert vehicles to VehicleInfo format
    const vehiclesInfo: VehicleInfo[] = vehicleIdentifiers.map(id => ({
      plate: id.identifierNumber,
      make: id.make || '',
      model: id.model || '',
      year: id.year || '',
      color: id.color || ''
    }));

    // Create contact object
    const contact: TennatAccountContactInfo = {
      contactName: `${member.firstName} ${member.lastName}`.trim(),
      contactFirstName: member.firstName || '',
      contactLastName: member.lastName || '',
      description: member.comments || '',
      rate: rate.code,
      rate_desc: rate.description,
      rate_amount: rate.unitprice,
      rateDisplay: rate.description,
      ar_type: 'Monthly',
      phones: member.telephone || '',
      extensions: '',
      emails: member.email || '',
      state: '',
      effectiveDates: member.startValidity ? this.formatDateForParkPro(member.startValidity) : new Date().toLocaleDateString('en-US'),
      terminationDates: member.endValidity ? this.formatDateForParkPro(member.endValidity) : '',
      keycards: '', // Will be populated by backend
      facility_code: '',
      plate: '',
      make: '',
      model: '',
      year: '',
      color: '',
      keycardsInfo: keycardsInfo,
      vehiclesInfo: vehiclesInfo,
      updateOnSurepark: true, //Setting this flag to `true` will save all identifiers in mapping table and update tenant contact on ppro
      // Additional required fields
      access_level: '',
      wps_section: '',
      paidDates: ''
    };

    return contact;
  }

  formatDateForParkPro(dateString: string): string {
    // Convert from YYYY-MM-DD to MM/DD/YYYY
    const dateObj = new Date(dateString);
    const formattedDate = (dateObj.getUTCMonth()+1)+"/"+dateObj.getUTCDate()+"/"+dateObj.getUTCFullYear();

    return formattedDate;
  }

  async addSelectedMissingContacts(): Promise<void> {
    //this flag will Force contact list component to re-load contacts when we set it back to false
    this.mergingContact = true;

    // Get selected rate details
    const selectedRate = this.availableRates.find(rate => rate.code === this.selectedDefaultRate);

    if (!selectedRate) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please select a rate for the new contacts'
      });
      return;
    }

    // Get selected members
    const membersToAdd = this.missingSureparkMembers.filter((member, index) =>
      this.selectedMissingMembers[index]
    );

    if (membersToAdd.length === 0) {
      this.messageService.add({
        severity: 'warn',
        detail: 'Please select at least one member to add'
      });
      return;
    }

    this.addingMissingContacts = true;

    try {

      // Convert SUREPARK members to ParkPro contact format
      const newContacts = membersToAdd.map(member =>
        this.convertSureparkMemberToContact(member, selectedRate)
      );

      // Get current contacts
      const currentContacts = this.allContacts || [];

      // Combine with new contacts
      const updatedContacts = [...currentContacts, ...newContacts];

      // During development we might not want to save contacts.
      const disableSaveTenantContactsDuringTesting = false;

      if (disableSaveTenantContactsDuringTesting) {
        console.log(`[TESTING] Not saving contacts with saveTenantContacts() due to testing flag.`);
        // debugger;
      } else {
        // Save contacts using the existing API
        await this.trpcService.trpc.authRouter.saveTenantContacts.mutate({
          contacts: updatedContacts,
          suite: this.queryParams.suite,
          account: this.queryParams.account
        });
      }


      this.messageService.add({
        severity: 'success',
        detail: `Successfully added ${membersToAdd.length} contact(s) to ParkPro`
      });

      // Close dialog
      this.showAddMissingContactsDialog = false;

      // Refresh the data
      await this.refreshContactsData();

    } catch (error: any) {
      console.error('Error adding missing contacts:', error);
      this.messageService.add({
        severity: 'error',
        detail: error.message || 'Failed to add missing contacts'
      });
    } finally {
      this.mergingContact = false;
      this.addingMissingContacts = false;
    }
  }

  async refreshContactsData(): Promise<void> {
    // Reload profile contacts
    await this.getProfileContacts(this.invoiceDetails);

    // Re-run matching logic
    await this.getMatchingParkproContacts();

    // Trigger refresh of contacts list component if needed
    // The contacts-list component will automatically refresh when the parent data changes
  }
}
